/**
 * Shared helpers for recovering from stale-deploy chunk/asset load failures.
 * Used by main.js (reload on unhandledrejection) and newrelic.ts (onerror + skip reporting).
 */

export const CHUNK_RELOAD_KEY = 'fe-app-chunk-reload-done'

const CHUNK_MESSAGE_REGEX =
  /Failed to fetch dynamically imported module|Importing a module script failed|Unable to preload CSS/
const ASSET_URL_REGEX = /fe-app-assets\//

/**
 * @param {string} [url] - URL to check
 * @returns {boolean}
 */
export function isAssetUrl(url) {
  return typeof url === 'string' && ASSET_URL_REGEX.test(url)
}

/**
 * @param {unknown} reason - Rejection reason or error object
 * @param {string} [source] - Optional URL (e.g. from window.onerror)
 * @returns {boolean}
 */
export function isChunkOrAssetFailure(reason, source) {
  let msg = ''
  if (typeof reason === 'string') {
    msg = reason
  } else if (reason?.message != null) {
    msg = String(reason.message)
  } else if (reason?.description != null) {
    msg = String(reason.description)
  }

  let url = source || ''
  if (!url && reason?.target?.href) url = reason.target.href
  if (!url && reason?.url != null) url = String(reason.url)
  if (!url && reason?.stack != null) url = String(reason.stack)

  const hasAssetUrl = isAssetUrl(url)
  const isChunkMessage = CHUNK_MESSAGE_REGEX.test(msg)
  const isFailedFetchOnAsset = /Failed to fetch/.test(msg) && hasAssetUrl
  return isChunkMessage || isFailedFetchOnAsset
}

/**
 * Reloads the page once per session when called for chunk/asset errors.
 * Safe to call multiple times: after the first reload (or when the key is already set), it no-ops.
 * @returns {void}
 */
export function tryReloadForChunkError() {
  if (typeof globalThis.window === 'undefined') return
  if (globalThis.sessionStorage.getItem(CHUNK_RELOAD_KEY)) return
  globalThis.sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
  globalThis.window.location.reload()
}
